import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChatCircleDotsIcon, DownloadSimpleIcon, WarningIcon } from '@phosphor-icons/react';
import { ApiError, Button, StatusChip } from '@smena/ui';
import { fetchMaxDialogs, fetchMaxSessions, importMaxHistory } from './api';

/**
 * Импорт переписок из личного аккаунта MAX.
 *
 * Честная рамка: протокол MAX снят реверс-инжинирингом, и опкод истории чата (49) до этой
 * правки ни разу не вызывался - на живом аккаунте путь не проверен. Поэтому кнопка есть,
 * но она не притворяется гарантированно рабочей: если формат ответа окажется другим,
 * панель покажет, ЧТО именно не сошлось (diagnostic из шлюза), и правка декодера займёт
 * минуты вместо вечера вслепую.
 *
 * Тихо показать «импортировано 0» было бы хуже, чем не делать кнопку вовсе.
 */
export function MaxHistoryImportCard() {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [diagnostics, setDiagnostics] = useState<string[]>([]);

  const sessionsQ = useQuery({ queryKey: ['max-personal'], queryFn: fetchMaxSessions });
  const session = sessionsQ.data?.items.find((s) => s.status === 'authorized');

  const dialogsQ = useQuery({
    queryKey: ['max-dialogs', session?.sessionId],
    queryFn: () => fetchMaxDialogs(session!.sessionId, 100),
    enabled: Boolean(session),
  });

  const importMut = useMutation({
    mutationFn: () => importMaxHistory(session!.sessionId, [...selected]),
    onSuccess: (r) => {
      setSelected(new Set());
      setDiagnostics(r.diagnostics);
      if (r.imported) {
        toast.success(`В базу знаний добавлено переписок: ${r.imported} (${r.messages} сообщений)`);
      } else if (r.diagnostics.length) {
        toast.error('Формат ответа MAX не распознан - подробности ниже');
      } else {
        toast.error('Ничего не добавлено - выбранные диалоги оказались пустыми');
      }
      if (r.skipped) toast.warning(`Пропущено коротких диалогов: ${r.skipped}`);
      for (const e of r.errors.slice(0, 3)) toast.error(e);
      void qc.invalidateQueries({ queryKey: ['kb-documents'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Импорт не удался'),
  });

  function toggle(chatId: number): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(chatId)) next.delete(chatId);
      else next.add(chatId);
      return next;
    });
  }

  const dialogs = dialogsQ.data?.items ?? [];

  return (
    <div className="rounded-md border border-line bg-panel2/40 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold">
        <ChatCircleDotsIcon weight="bold" className="size-4 text-accent-fg" /> Из личного MAX
        <StatusChip tone="amber">на живом аккаунте не проверено</StatusChip>
      </div>

      {!sessionsQ.data?.available ? (
        <div className="text-xs text-muted">
          Шлюз личных номеров не поднят на этом контуре - импорт недоступен.
        </div>
      ) : !session ? (
        <div className="text-xs text-muted">
          Личный номер MAX не подключён. Подключите его в разделе «Интеграции» - после этого
          переписки можно будет забрать сюда.
        </div>
      ) : (
        <>
          <p className="mb-2 text-xs text-muted">
            Выберите переписки с гостями. Чтение истории в MAX реализовано по нашему разбору
            протокола и на живом аккаунте ещё не подтверждено: если что-то пойдёт не так,
            здесь появится точная причина.
          </p>

          {dialogsQ.isLoading ? (
            <div className="text-xs text-muted">Читаем список диалогов…</div>
          ) : dialogsQ.isError ? (
            <div className="flex items-start gap-2 rounded-md border border-red/40 bg-red/10 p-2 text-[11px] text-red">
              <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
              <span>
                Не удалось получить диалоги:{' '}
                {dialogsQ.error instanceof ApiError ? dialogsQ.error.message : 'ошибка шлюза'}
              </span>
            </div>
          ) : (
            <>
              {dialogsQ.data?.diagnostic ? (
                <div className="mb-2 flex items-start gap-2 rounded-md border border-amber/40 bg-amber/10 p-2 text-[11px] text-amber">
                  <WarningIcon weight="fill" className="mt-0.5 size-3.5 shrink-0" />
                  <span className="break-words">{dialogsQ.data.diagnostic}</span>
                </div>
              ) : null}

              {dialogs.length === 0 ? (
                <div className="text-xs text-muted">Диалогов не нашлось.</div>
              ) : (
                <ul className="mb-2 flex max-h-64 flex-col gap-1 overflow-auto">
                  {dialogs.map((d) => (
                    <li key={d.chatId}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-panel2">
                        <input
                          type="checkbox"
                          checked={selected.has(d.chatId)}
                          onChange={() => toggle(d.chatId)}
                          className="size-4 shrink-0 accent-[var(--accent)]"
                        />
                        <span className="min-w-0 flex-1 truncate">{d.title}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  disabled={selected.size === 0 || importMut.isPending}
                  onClick={() => importMut.mutate()}
                >
                  <DownloadSimpleIcon weight="bold" className="size-4" />
                  {importMut.isPending ? 'Забираем…' : 'Забрать в базу знаний'}
                </Button>
                <StatusChip tone={selected.size ? 'accent' : 'muted'}>
                  выбрано: {selected.size}
                </StatusChip>
              </div>
            </>
          )}

          {diagnostics.length ? (
            <div className="mt-2 flex flex-col gap-1 rounded-md border border-amber/40 bg-amber/10 p-2 text-[11px] text-amber">
              <span className="font-medium">Формат ответа MAX отличается от ожидаемого</span>
              {diagnostics.slice(0, 5).map((d, i) => (
                <span key={i} className="break-words">
                  {d}
                </span>
              ))}
              <span className="mt-0.5">
                Перешлите этот текст нам - по нему декодер правится точечно.
              </span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
